import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export function Hero() {
  const scrollToContact = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      className="relative min-h-[100dvh] flex items-center justify-center pt-20 pb-28 md:pb-32 overflow-hidden"
    >
      {/* Readability gradient (the shared starfield is provided by Home.tsx) */}
      <div className="absolute inset-0 z-0 pointer-events-none bg-gradient-to-b from-background/0 via-background/30 to-background/60" />

      {/* HUD overlay — faint grid + corner reticles */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(4,6,14,0.55), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute top-24 right-6 md:right-12 z-[1] pointer-events-none opacity-30 font-mono text-[10px] tracking-widest text-primary"
      >
        ◢ NAV ◣
      </div>
      <div
        aria-hidden="true"
        className="absolute bottom-6 left-6 md:left-12 z-[1] pointer-events-none opacity-30 font-mono text-[10px] tracking-widest text-cyan-400"
      >
        — HEADING 0042 —
      </div>

      <div className="container relative z-10 mx-auto px-6 md:px-12 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Boutique AI Consulting, Houston, TX
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display font-bold tracking-tight text-foreground mb-8 leading-[1.1]">
            Plot a course through the{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-300">
              AI frontier.
            </span>
          </h1>

          <p className="text-lg md:text-2xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
            Agentic tools, modern AI, real outcomes for small and mid-sized businesses. We build the bridge between tomorrow's technology and today's operations.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="h-14 px-8 text-lg group" onClick={scrollToContact}>
              Engage Systems
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-8 text-lg border-primary/20 hover:bg-primary/10"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              View Our Capabilities
            </Button>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 hidden flex-col items-center gap-2 text-muted-foreground [@media(min-height:780px)]:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
      >
        <span className="text-[0.625rem] uppercase tracking-widest font-mono">Scroll</span>
        <div className="w-[1px] h-10 bg-gradient-to-b from-primary/50 to-transparent" />
      </motion.div>
    </section>
  );
}
